import { ArrowLeft, ChevronDown } from 'lucide-react';
import * as React from 'react';
import { memo, useState, useEffect, useRef } from 'react';

import { Link, Button } from '@/components/design-system';
import {
  GAP_SCALE,
  TYPE_SCALE,
  FONT_WEIGHTS,
  RADIUS_SCALE,
  PADDING_X,
  PADDING_Y,
  COLOR_SCALE,
  SIZE_SCALE,
  POSITION_SCALE,
  MARGIN_TOP,
  PADDING_Y as PY,
  FLEX_SHRINK,
} from '@/components/design-system/tokens';
import { SUBDOMAIN } from '@/constants';
import { createPageUrl, normalizeLocale } from '@/utils';

const marqueeStyle = `
@keyframes logo-marquee {
  0% { transform: translateX(0); }
  100% { transform: translateX(calc(-1 * var(--marquee-total-width, 0px))); }
}
.animate-logo-marquee {
  animation: logo-marquee var(--marquee-duration, 12s) linear infinite;
}
`;

const PROVIDER_BRAND_SCROLL_THRESHOLD = 16;

// Region selector component
const RegionSelector = memo(
  ({
    headerOnLight,
    t,
  }: {
    headerOnLight: boolean;
    t: (namespace: string, key: string, fallback: string) => string;
  }) => {
    const [regionMenuOpen, setRegionMenuOpen] = useState(false);
    const regionDropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (!regionMenuOpen) return;

      const handleClickOutside = (event: MouseEvent) => {
        if (
          regionDropdownRef.current &&
          !regionDropdownRef.current.contains(event.target as Node)
        ) {
          setRegionMenuOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [regionMenuOpen]);

    const koreaLabel = String(t('common', 'region_korea', 'Korea'));
    const vietnamLabel = String(t('common', 'region_vietnam', 'Vietnam'));
    const currentRegion = SUBDOMAIN === 'vn' ? vietnamLabel : koreaLabel;

    const optionsRegion = [
      {
        label: koreaLabel,
        url: 'https://medipaths.co',
      },
      {
        label: vietnamLabel,
        url: 'https://vn.medipaths.co',
      },
    ];

    return (
      <div ref={regionDropdownRef} className={`${POSITION_SCALE.relative} ${FLEX_SHRINK[0]}`}>
        <button
          type="button"
          onClick={() => setRegionMenuOpen(prev => !prev)}
          className={`
        flex items-center ${GAP_SCALE['1']} ${PADDING_X['3']} ${PADDING_Y['1.5']} ${RADIUS_SCALE.full} ${TYPE_SCALE.xs} ${FONT_WEIGHTS.semibold} uppercase tracking-[0.2em]
        transition-all duration-200 border
        ${
          headerOnLight
            ? `${COLOR_SCALE['header-light']}`
            : `${COLOR_SCALE['header-dark']} border-white/40 hover:bg-white/10`
        }
      `}
          aria-haspopup="menu"
          aria-expanded={regionMenuOpen}
        >
          <span>{currentRegion}</span>
          <ChevronDown
            className={`${SIZE_SCALE['3']} ${SIZE_SCALE['3']} transition-transform duration-200 ${
              regionMenuOpen ? 'rotate-180' : ''
            }`}
          />
        </button>

        <div
          className={`
        absolute right-0 ${MARGIN_TOP['2']} min-w-[var(--component-width-lg)] rounded-xl overflow-hidden z-[1000]
        backdrop-blur-xl
        transition-all duration-200 origin-top-right
        ${regionMenuOpen ? 'opacity-100 scale-100 visible' : 'opacity-0 scale-95 invisible'}
        ${
          headerOnLight
            ? `${COLOR_SCALE['dropdown-bg']} shadow-lg`
            : `${COLOR_SCALE['tooltip-bg']}/95 border border-white/10 shadow-xl`
        }
      `}
          role="menu"
        >
          <div className={PY['1']}>
            {optionsRegion.map((option, _index) => {
              const isDisabled = option.label === currentRegion;
              return (
                <button
                  key={option.url}
                  className={`
              ${isDisabled ? 'pointer-events-none' : ''}
            block ${PADDING_X['4']} ${PADDING_Y['2']} ${TYPE_SCALE.xs} ${FONT_WEIGHTS.semibold} uppercase tracking-[0.2em]
            transition-colors duration-200
            ${headerOnLight ? `${COLOR_SCALE['dropdown-item']}` : `${COLOR_SCALE['header-dark']}`}
          `}
                  role="menuitem"
                  onClick={() => {
                    if (!isDisabled) {
                      window.location.href = option.url;
                    }
                    setRegionMenuOpen(false);
                  }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
);

RegionSelector.displayName = 'RegionSelector';

// Helper function to get provider display name
const getProviderDisplayName = (
  currentPageName: string,
  providerBrandName?: string,
  providerBranding?: { name?: string; slug?: string; locale?: string } | null
): string => {
  const providerDisplayNameRaw =
    (currentPageName === 'ProviderDetail' ? providerBrandName : null) ??
    (currentPageName === 'ProviderDetail' ? providerBranding?.name : null) ??
    (currentPageName === 'ProviderDetail' ? providerBranding?.slug : null) ??
    '';
  return providerDisplayNameRaw.trim();
};

// Helper function to get provider locale
const getProviderLocale = (
  providerBranding?: { name?: string; slug?: string; locale?: string } | null
): string => {
  const browserLocale = typeof navigator !== 'undefined' ? navigator.language : undefined;
  return (
    normalizeLocale(providerBranding?.locale, normalizeLocale(browserLocale, 'en-US')) ?? 'en-US'
  );
};

// Helper function to handle marquee animation
const getContainerWidth = (element: HTMLElement): number => {
  return element.parentElement?.parentElement?.parentElement?.parentElement?.offsetWidth ?? 0;
};

const setupMarqueeStyles = (element: HTMLElement, totalTravel: number, overflow: number): void => {
  element.style.setProperty('--marquee-total-width', `${totalTravel}px`);
  element.style.setProperty('--marquee-duration', overflow > 0 ? '12s' : '10s');
};

const handleNoOverflow = (element: HTMLElement, text: string): void => {
  element.classList.remove('animate-logo-marquee');
  element.textContent = text;
  element.style.width = 'auto';
};

const handleOverflow = (element: HTMLElement, text: string): void => {
  element.classList.add('animate-logo-marquee');
  const duplicatedText = text + ' ' + text;
  element.textContent = duplicatedText;
  element.style.width = 'auto';
};

const handleMarqueeAnimation = (
  nameRef: React.RefObject<HTMLSpanElement>,
  providerDisplayNameUpper: string,
  showProviderName: boolean
): void => {
  if (!nameRef.current || !showProviderName) {
    if (nameRef.current) {
      nameRef.current.classList.remove('animate-logo-marquee');
    }
    return;
  }

  const element = nameRef.current;
  const containerWidth = getContainerWidth(element);
  const contentWidth = element.scrollWidth;
  const overflow = Math.max(contentWidth - containerWidth, 0);
  const totalTravel = overflow + containerWidth;

  setupMarqueeStyles(element, totalTravel, overflow);

  if (overflow <= 0) {
    handleNoOverflow(element, providerDisplayNameUpper);
  } else {
    handleOverflow(element, providerDisplayNameUpper);
  }
};

interface LogoProps {
  currentPageName: string;
  headerOnLight: boolean;
  providerBrandName?: string | undefined;
  providerBranding?: {
    name?: string;
    slug?: string;
    locale?: string;
  } | null;
  scrollY: number;
  t: (namespace: string, key: string, fallback: string) => string;
}

const getProviderInfo = (
  currentPageName: string,
  providerBrandName?: string,
  providerBranding?: {
    name?: string;
    slug?: string;
    locale?: string;
  } | null
) => {
  const providerDisplayName = getProviderDisplayName(
    currentPageName,
    providerBrandName,
    providerBranding
  );
  const providerLocale = getProviderLocale(providerBranding);
  const providerDisplayNameUpper = providerDisplayName
    ? providerDisplayName.toLocaleUpperCase(providerLocale)
    : '';
  const hasProviderName = Boolean(currentPageName === 'ProviderDetail' && providerDisplayNameUpper);

  return {
    providerDisplayName,
    providerLocale,
    providerDisplayNameUpper,
    hasProviderName,
  };
};

const getLogoConfig = (
  currentPageName: string,
  t: (namespace: string, key: string, fallback: string) => string
) => {
  const homeLabel = String(t('ui_components', 'medipaths_home', 'Medipaths Home'));
  const showBackButton = currentPageName === 'MedicalProcedureDetail';

  return { homeLabel, showBackButton };
};

export const Logo = memo(
  ({
    currentPageName,
    headerOnLight,
    providerBrandName,
    providerBranding = null,
    scrollY,
    t,
  }: LogoProps) => {
    const [hasScrolledPastThreshold, setHasScrolledPastThreshold] = useState(false);
    const nameRef = useRef<HTMLSpanElement>(null);

    const { providerDisplayNameUpper, hasProviderName } = getProviderInfo(
      currentPageName,
      providerBrandName,
      providerBranding
    );
    const { homeLabel, showBackButton } = getLogoConfig(currentPageName, t);

    useEffect(() => {
      if (showBackButton) {
        setHasScrolledPastThreshold(false);
        return;
      }

      if (!hasProviderName) {
        setHasScrolledPastThreshold(false);
        return;
      }

      setHasScrolledPastThreshold(scrollY > PROVIDER_BRAND_SCROLL_THRESHOLD);
    }, [scrollY, hasProviderName, showBackButton]);

    useEffect(() => {
      if (showBackButton || !hasProviderName) return;

      const handleScroll = () => {
        setHasScrolledPastThreshold(window.scrollY > PROVIDER_BRAND_SCROLL_THRESHOLD);
      };

      handleScroll();
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    }, [hasProviderName, showBackButton]);

    const showProviderName = hasProviderName && hasScrolledPastThreshold;

    useEffect(() => {
      if (showBackButton) return;
      handleMarqueeAnimation(nameRef, providerDisplayNameUpper, showProviderName);
    }, [providerDisplayNameUpper, showProviderName, showBackButton]);

    const logoShellBaseClasses = `
    relative inline-flex items-center justify-start h-14 flex-shrink-0
    ${TYPE_SCALE.xl} ${FONT_WEIGHTS.bold} uppercase transition-colors duration-300
    ${headerOnLight ? `${COLOR_SCALE.foreground}` : 'text-white'}
  `;

    const minWidth = '25px';
    const maxWidth = showProviderName ? 'min(65vw, 520px)' : '200px';
    const paddingRight = '0.5rem';

    const linkDynamicStyle = {
      minWidth,
      maxWidth,
      paddingRight,
    };

    if (showBackButton) {
      return (
        <Button
          variant="ghost"
          onClick={() => window.history.back()}
          className={`${TYPE_SCALE.xs} ${FONT_WEIGHTS.medium} transition-colors duration-300 flex items-center ${GAP_SCALE['2']} ${
            headerOnLight
              ? `${COLOR_SCALE.foreground} ${COLOR_SCALE['header-light']}`
              : `${COLOR_SCALE['header-dark']}`
          } ${RADIUS_SCALE.xl} ${PADDING_X['4']} ${PADDING_Y['2']}`}
          aria-label={String(t('aria_extended', 'go_back', 'Go back to previous page'))}
        >
          <ArrowLeft className={`${SIZE_SCALE['4']} ${SIZE_SCALE['4']}`} />
          <span>{String(t('common', 'back', 'Back'))}</span>
        </Button>
      );
    }

    const handleLogoClick = (e: React.MouseEvent) => {
      // If we're already on the home page, scroll to top instead of navigating
      if (currentPageName === 'Home') {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      // If not on home page, allow normal navigation to home page
    };

    return (
      <div className={`flex items-center ${GAP_SCALE['3']} min-w-0`}>
        <Link
          to={createPageUrl('Home')}
          onClick={handleLogoClick}
          aria-label={homeLabel}
          className={`${logoShellBaseClasses} overflow-hidden`}
          style={linkDynamicStyle}
        >
          <style>{marqueeStyle}</style>
          <div className={`${POSITION_SCALE.relative} ${SIZE_SCALE.full}`}>
            <span
              className={`
              block w-full whitespace-nowrap overflow-hidden text-ellipsis
              transition-opacity duration-200 ease-out
              ${showProviderName ? 'opacity-0' : 'opacity-100'}
            `}
            >
              MEDIPATHS
            </span>

            {hasProviderName && (
              <div
                className={`
                absolute inset-0 flex flex-col justify-center items-center
                transition-opacity duration-300 ease-out pointer-events-none
                ${showProviderName ? 'opacity-100' : 'opacity-0'}
              `}
              >
                <span
                  className={`${TYPE_SCALE.xs} sm:${TYPE_SCALE.xs} tracking-[0.35em] leading-none text-current`}
                >
                  MEDIPATHS
                </span>
                <div className="overflow-hidden">
                  <span
                    ref={nameRef}
                    className={`${TYPE_SCALE.xs} sm:${TYPE_SCALE.sm} ${FONT_WEIGHTS.semibold} uppercase whitespace-nowrap leading-none tracking-wide block`}
                    title={providerDisplayNameUpper}
                  >
                    {providerDisplayNameUpper}
                  </span>
                </div>
              </div>
            )}
          </div>
        </Link>

        <RegionSelector headerOnLight={headerOnLight} t={t} />
      </div>
    );
  }
);

Logo.displayName = 'Logo';
